import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import EventIcon from '@mui/icons-material/Event';
import PaymentsIcon from '@mui/icons-material/Payments';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import CallIcon from '@mui/icons-material/Call';
import api from '../services/api';
import { formatDate } from '../utils/format';
import { useNotification } from '../context/NotificationContext';

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  color?: string;
}> = ({ icon, label, value, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 2, sm: 2.5 } }}>
      <Box
        sx={{
          width: { xs: 36, sm: 44 },
          height: { xs: 36, sm: 44 },
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: color || 'primary.main',
          color: 'white',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.3rem' }, lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

export const CollectiveHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showError } = useNotification();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/collectives/${id}/history`);
        setData(response.data);
      } catch (error: any) {
        console.error('Error fetching collective history:', error);
        showError(error.response?.data?.error || 'Не удалось загрузить историю коллектива');
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchHistory();
    }
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Не удалось загрузить данные коллектива</Alert>
      </Box>
    );
  }

  const { collective, summary, events } = data;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Tooltip title="Назад">
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
          {collective.name}
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Grid container spacing={1.5}>
            {collective.city && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOnIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">Город:</Typography>
                  <Typography variant="body1">{collective.city}</Typography>
                </Box>
              </Grid>
            )}
            {collective.school && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">Школа:</Typography>
                  <Typography variant="body1">{collective.school}</Typography>
                </Box>
              </Grid>
            )}
            {collective.contacts && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CallIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">Контакты:</Typography>
                  <Typography variant="body1">{collective.contacts}</Typography>
                </Box>
              </Grid>
            )}
            {collective.accessory && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MusicNoteIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">Принадлежность:</Typography>
                  <Typography variant="body1">{collective.accessory}</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={3}>
          <StatCard icon={<EventIcon />} label="Мероприятий" value={summary.totalEvents} color="#8b5cf6" />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatCard icon={<MusicNoteIcon />} label="Регистраций" value={summary.totalRegistrations} color="#0ea5e9" />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatCard icon={<GroupsIcon />} label="Участников за всё время" value={summary.totalParticipantsAllTime} color="#22c55e" />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatCard icon={<GroupsIcon />} label="Уникальных участников" value={summary.uniqueParticipants} color="#3b82f6" />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatCard icon={<MusicNoteIcon />} label="Танцев" value={summary.totalDances} color="#ec4899" />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatCard icon={<MusicNoteIcon />} label="Дисциплин" value={summary.totalDisciplines} color="#f59e0b" />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatCard icon={<PaymentsIcon />} label="Оплачено (выст.)" value={`${summary.totalPaidPerformance.toLocaleString('ru-RU')} ₽`} color="#10b981" />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatCard icon={<PaymentsIcon />} label="Оплачено (Д/М)" value={`${summary.totalPaidDiplomas.toLocaleString('ru-RU')} ₽`} color="#f43f5e" />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Участие по годам
      </Typography>

      {events.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">У коллектива пока нет зарегистрированных номеров.</Typography>
        </Paper>
      ) : (
        events.map((entry: any) => (
          <Accordion key={`${entry.eventId}-${entry.year}`} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: '100%' }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                  {entry.year}
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                  {entry.eventName}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={0.75}>
                <Grid item xs={12}>
                  {entry.eventDate && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Дата: {formatDate(entry.eventDate)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Регистраций</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{entry.registrations}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Участников</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{entry.totalParticipants}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Оплачено</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {entry.totalAmountPaid > 0 ? `${entry.totalAmountPaid.toLocaleString('ru-RU')} ₽` : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography variant="body2" color="text.secondary">Номера:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: { xs: 'flex-start', sm: 'flex-end' }, mt: 0.5 }}>
                    {(entry.dances || []).slice(0, 6).map((d: string) => (
                      <Chip key={d} label={d} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};